import getFromXlsx from '/shared/functions/getFromXlsx';
import type { BulkUploadRef, XlsxCallback } from '/business/shared/functions/types';
import { getXlsxString } from '/business/shared/functions/types';

const EMPLOYMENT_TYPES = ['full_time', 'part_time'] as const;

type EmploymentType = (typeof EMPLOYMENT_TYPES)[number];

type DistanceUnit = 'km' | 'miles';

type DistanceRow = {
  displayName: string;
  name: string;
  unit: string;
  value: number;
};

type CommuterFactor = {
  displayName: string;
  name: string;
};

type ParsedEmployeeCommute = {
  firstName: string;
  distance?: string;
  numberOfUnits?: number;
  calculationDistanceUnit?: string;
  selectedDistanceUnit?: DistanceUnit;
  commuterFactorName?: string | null;
  employmentType?: EmploymentType | null;
};

type EmployeeCommuteRow = {
  commuterFactor?: string | null;
  employmentType?: EmploymentType | null;
  firstName: string;
  distance?: string;
  distanceUnit?: DistanceUnit;
};

export default async function getEmployeeCommutesFromXlsx({
  bulkUploadRef,
  t,
  distances,
  commuterFactors,
  callback = null,
}: {
  bulkUploadRef: BulkUploadRef;
  t: (key: string) => string;
  distances: Record<DistanceUnit, DistanceRow[]>;
  commuterFactors: CommuterFactor[];
  callback?: XlsxCallback<EmployeeCommuteRow> | null;
}) {
  const file = bulkUploadRef.current.getSelectedFile();
  const translitFactors = commuterFactors.map((factor) => {
    return {
      ...factor,
      translitName: t(`business:emissionFactors.transportModes.${factor.displayName}`),
    };
  });
  const translitEmploymentTypes = EMPLOYMENT_TYPES.map((name) => {
    return {
      name,
      translitName: t(`business:QuestionPage.forms.commutes.employmentType.${name}`),
    };
  });
  // Get the raw xlsx dataset
  const getLangLookup = (await getFromXlsx({
    file,
    worksheet: 0,
    rowStart: 1,
    columnStart: 9,
    columnEnd: 9,
    componentCallback: (error = null, rows = []) => {
      const matrix: Record<string, string> = {};
      rows.map((row) => {
        const translatedVal = getXlsxString(row, '__EMPTY').trim();
        matrix[translatedVal] = getXlsxString(row, '__EMPTY_1').trim();
        return row;
      });
      return matrix;
    },
  })) as Record<string, string>;

  const xlsxRows = await getFromXlsx({
    file,
    worksheet: 0,
    rowStart: 11,
    columnEnd: 4,
    componentCallback: (error = null, rows = []) => {
      // Handle if the code is being used in a sync. flow (with no callbacks)
      if (callback === null) {
        return rows;
      }
      if (error !== null) {
        return callback(error, []);
      }
      let componentError: string | null = null;
      const parsedRows = rows
        .map((row) => {
          const parsedRow: ParsedEmployeeCommute = {
            firstName: getXlsxString(row, '__EMPTY'),
          };

          // Trim down the values as basic auto-correction
          // Also pull distance out as seperate props so we can handle the unit ambiguities
          const distanceMiles = getXlsxString(row, '__EMPTY_2').trim();
          const distanceKms = getXlsxString(row, '__EMPTY_1').trim();
          let transportMode = getXlsxString(row, '__EMPTY_3').trim();
          if (getLangLookup[transportMode]) {
            transportMode = getLangLookup[transportMode];
          }
          let employmentType = getXlsxString(row, '__EMPTY_4').trim();
          if (getLangLookup[employmentType]) {
            employmentType = getLangLookup[employmentType];
          }
          parsedRow.firstName = parsedRow.firstName.trim();

          // Identify if km or miles was selected for this row
          const selectedDistanceUnit: DistanceUnit = distanceKms.length > 1 ? 'km' : 'miles';

          if (distanceMiles && distanceKms) {
            // A row has been entered with both miles and km
            componentError = 'ERRINVALIDTEMPLATEINVALIDROW';
          }

          // Now we have pulled what should be mapped in as the value
          // lets ensure its an available option on the website
          const matchedDistanceRow =
            distances[selectedDistanceUnit].find((distanceRow) => {
              const { displayName } = distanceRow;
              /*
           Remove the 'human readable text'
           So < 20 'foo', becomes '<20'
           20 - 50 'foo', becomes '20-50'
           >50 'foo', becomes '>50'
           */
              //
              const displayNameFormatted = displayName.replace(/[^0-9<>\-.]/g, '');
              if (selectedDistanceUnit === 'km') {
                // Strip spaces from the static version of the distance, so we have a like-for-like whitelist check
                const distanceKmsFormatted = distanceKms.replace(/ /g, '');
                return displayNameFormatted === distanceKmsFormatted;
              }
              // Strip spaces from the static version of the distance, so we have a like-for-like whitelist check
              const distanceMilesFormatted = distanceMiles.replace(/ /g, '');
              return displayNameFormatted === distanceMilesFormatted;
            }) || null;

          // This check fails if the bounding in excel does not match what is in the website anymore
          if (!matchedDistanceRow) {
            return null;
          }

          // Now we have matched the provided data with the correct range from the site
          // Lets pull out the distance ('high/medium etc') so we can match with the same form inputs
          // and the number of units in the same format
          parsedRow.distance = matchedDistanceRow.name;
          parsedRow.numberOfUnits = matchedDistanceRow.value;
          parsedRow.calculationDistanceUnit = matchedDistanceRow.unit;
          parsedRow.selectedDistanceUnit = selectedDistanceUnit;

          // Now pass all props back into the page
          parsedRow.commuterFactorName =
            translitFactors?.find(({ translitName }) => translitName === transportMode)?.name || null;

          // This check fails if the travel mode from the xlsx from the sheet failed to match in the website
          if (!parsedRow.commuterFactorName) {
            return null;
          }

          parsedRow.employmentType =
            translitEmploymentTypes.find(({ translitName }) => translitName === employmentType)?.name || null;

          if (!parsedRow.employmentType) {
            return null;
          }
          return parsedRow;
        })
        .filter((row) => {
          return row !== null;
        })
        .map((row) => {
          // Re-map to ensure the output rows match the output expected by as generic row propNames
          // + 'calculationDataCallback'
          return {
            commuterFactor: row.commuterFactorName,
            employmentType: row.employmentType,
            firstName: row.firstName,
            distance: row.distance,
            distanceUnit: row.selectedDistanceUnit,
          };
        });

      if (parsedRows.length !== rows.length) {
        // There have been invalid rows
        componentError = 'ERRINVALIDTEMPLATEINVALIDROW';
      }

      if (parsedRows.length === 0) {
        // No valid row!
        componentError = 'ERRINVALIDTEMPLATE';
      }

      // Apply page specific handlers (if any)
      return callback(componentError, parsedRows.reverse());
    },
  });
  return xlsxRows;
}
